const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Single owner for every modal's keyboard, focus, backdrop and scroll lifecycle.
 * Feature controllers own modal content; this class owns modal behaviour.
 *
 * A feature may replace its modal DOM while refreshing data. The lifecycle treats
 * an overlay replacement inside the same host as the same modal session: the
 * original opener remains the return-focus target and focus is not returned until
 * the host is actually closed.
 */
export class ModalLifecycle {
  constructor(root) {
    this.root = root;
    this.stack = [];
    this.observer = new MutationObserver(() => this.sync());
    this.boundKeydown = event => this.keydown(event);
    this.boundClick = event => this.click(event);
    this.boundMousedown = event => this.mousedown(event);
    this.mousedownOverlay = null;
  }

  start() {
    this.observer.observe(this.root, { childList: true, subtree: true });
    document.addEventListener('keydown', this.boundKeydown, true);
    this.root.addEventListener('click', this.boundClick, true);
    this.root.addEventListener('mousedown', this.boundMousedown, true);
    this.sync();
  }

  destroy() {
    this.observer.disconnect();
    document.removeEventListener('keydown', this.boundKeydown, true);
    this.root.removeEventListener('click', this.boundClick, true);
    this.root.removeEventListener('mousedown', this.boundMousedown, true);
    this.stack = [];
    document.body.classList.remove('rc5-modal-open');
  }

  entryForNewOverlay(overlay, previous, claimed) {
    const existing = previous.find(entry => entry.overlay === overlay);
    if (existing) {
      claimed.add(existing);
      return existing;
    }

    const host = overlay.parentElement;
    const replacement = previous.find(entry => !claimed.has(entry) && entry.host === host);
    if (replacement) {
      claimed.add(replacement);
      return { overlay, host, returnFocus: replacement.returnFocus };
    }

    return {
      overlay,
      host,
      returnFocus: document.activeElement instanceof HTMLElement ? document.activeElement : null,
    };
  }

  sync() {
    const overlays = [...this.root.querySelectorAll('.rc5-modal-overlay')];
    const previous = this.stack;
    const claimed = new Set();
    const next = overlays.map(overlay => this.entryForNewOverlay(overlay, previous, claimed));

    for (const entry of previous) {
      if (claimed.has(entry)) continue;
      if (entry.returnFocus?.isConnected) {
        requestAnimationFrame(() => entry.returnFocus?.focus?.({ preventScroll: true }));
      }
    }

    const previousOverlays = new Set(previous.map(entry => entry.overlay));
    this.stack = next;
    document.body.classList.toggle('rc5-modal-open', this.stack.length > 0);

    const newest = this.stack.at(-1);
    if (newest && !previousOverlays.has(newest.overlay)) {
      requestAnimationFrame(() => {
        const dialog = newest.overlay.querySelector('[role="dialog"]');
        const preferred = newest.overlay.querySelector('[autofocus], [data-modal-initial-focus]');
        const first = newest.overlay.querySelector(FOCUSABLE);
        (preferred || first || dialog)?.focus?.({ preventScroll: true });
      });
    }
  }

  top() { return this.stack.at(-1)?.overlay || null; }

  close(overlay) {
    const action = overlay?.dataset.modalCloseAction;
    if (!action) return;
    const button = overlay.querySelector(`[data-action="${CSS.escape(action)}"]`);
    button?.click();
  }

  mousedown(event) {
    const overlay = event.target.closest?.('.rc5-modal-overlay');
    this.mousedownOverlay = event.target === overlay ? overlay : null;
  }

  click(event) {
    const overlay = event.target.closest?.('.rc5-modal-overlay');
    if (!overlay || overlay !== this.top()) return;
    if (event.target === overlay && this.mousedownOverlay === overlay) this.close(overlay);
  }

  keydown(event) {
    const overlay = this.top();
    if (!overlay) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close(overlay);
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = [...overlay.querySelectorAll(FOCUSABLE)].filter(element => element.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      overlay.querySelector('[role="dialog"]')?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}
